import { useSelector } from 'react-redux';
import { selectors } from '@altermaind/core/i18n';
import { DEFAULT_LANGUAGE } from '../constants';

type UseLocalizedMonthName = {
  stringToMonthName: (date?: string) => string | undefined;
  dateToMonthName: (date?: Date) => string | undefined;
};

/**
 * Provides utility functions to localize month names based on the current language.
 * The language is determined using the `react-redux` selector or falls back to a default language.
 *
 * Dependencies:
 * - `react-redux` for accessing the current language via a selector.
 * - `@altermaind/core/i18n` for language-related selectors.
 * - `Intl.DateTimeFormat` for formatting dates into localized month names.
 *
 * @typedef {Object} UseLocalizedMonthName
 * @property {function(string | undefined): (string | undefined)} stringToMonthName - Converts a date string to a localized month name.
 * @property {function(Date | undefined): (string | undefined)} dateToMonthName - Converts a Date object to a localized month name.
 *
 * @returns {UseLocalizedMonthName} An object containing the utility functions.
 *
 * @example
 * import { useLocalizedMonthName } from '@/_shared/hooks';
 *
 * const { stringToMonthName, dateToMonthName } = useLocalizedMonthName();
 *
 * // Example with a date string
 * const monthNameFromString = stringToMonthName('2023-03-15'); // e.g., "March"
 *
 * // Example with a Date object
 * const monthNameFromDate = dateToMonthName(new Date(2023, 2, 15)); // e.g., "March"
 */
export const useLocalizedMonthName = (): UseLocalizedMonthName => {
  const language = useSelector(selectors.selectCurrentLanguage) || DEFAULT_LANGUAGE;

  const { format } = new Intl.DateTimeFormat(language, {
    month: 'long',
  });

  const stringToMonthName = (date?: string): string | undefined => {
    if (!date) {
      return undefined;
    }
    return format(new Date(date));
  };

  const dateToMonthName = (date?: Date): string | undefined => {
    if (!date) {
      return undefined;
    }
    return format(date);
  };

  return {
    stringToMonthName,
    dateToMonthName,
  };
};
